/**
 * Utility functions for converting plain text to HTML and detecting content type
 */

/**
 * Checks if content is already HTML
 */
export function isHtmlContent(content: string): boolean {
  if (!content) return false;

  // Check for common HTML tags
  const htmlTagPattern = /<\/?(?:p|h[1-6]|ul|ol|li|strong|em|br|div|span|a)[^>]*>/i;
  return htmlTagPattern.test(content);
}

/**
 * Converts plain text to HTML with automatic structure detection
 */
export function convertTextToHtml(text: string): string {
  if (!text) return '';

  // If already HTML, return as-is
  if (isHtmlContent(text)) {
    return text;
  }

  // Split by double line breaks to detect paragraphs
  const blocks = text.split(/\n\s*\n/);
  const htmlBlocks: string[] = [];

  for (let block of blocks) {
    block = block.trim();
    if (!block) continue;

    // Check if it's a numbered list (starts with "1." or "1)")
    if (/^\d+[\.\)]\s/.test(block)) {
      const listItems = block.split(/\n(?=\d+[\.\)]\s)/);
      const items = listItems
        .map(item => {
          const content = item.replace(/^\d+[\.\)]\s+/, '').trim();
          return `<li>${escapeHtml(content)}</li>`;
        })
        .join('\n');
      htmlBlocks.push(`<ol>\n${items}\n</ol>`);
      continue;
    }

    // Check if it's a bullet list (starts with -, *, or •)
    if (/^[-\*•]\s/.test(block)) {
      const listItems = block.split(/\n(?=[-\*•]\s)/);
      const items = listItems
        .map(item => {
          const content = item.replace(/^[-\*•]\s+/, '').trim();
          return `<li>${escapeHtml(content)}</li>`;
        })
        .join('\n');
      htmlBlocks.push(`<ul>\n${items}\n</ul>`);
      continue;
    }

    // Check if it looks like a heading (short line, possibly followed by content)
    const lines = block.split('\n');
    if (lines.length === 1 && block.length < 80 && !block.endsWith('.') && !block.endsWith(',')) {
      // Might be a heading
      htmlBlocks.push(`<h2>${escapeHtml(block)}</h2>`);
      continue;
    }

    // Check for lines that are all caps (likely headings)
    if (block === block.toUpperCase() && block.length > 3 && block.length < 80) {
      // Convert to title case for better readability
      const titleCase = block.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
      htmlBlocks.push(`<h2>${escapeHtml(titleCase)}</h2>`);
      continue;
    }

    // Process as paragraph with inline formatting
    let processedBlock = block;

    // Handle single line breaks within paragraph (replace with space)
    processedBlock = processedBlock.replace(/\n/g, ' ');

    // Handle bold text (**text** or __text__)
    processedBlock = processedBlock.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    processedBlock = processedBlock.replace(/__(.+?)__/g, '<strong>$1</strong>');

    // Handle italic text (*text* or _text_)
    processedBlock = processedBlock.replace(/\*(.+?)\*/g, '<em>$1</em>');
    processedBlock = processedBlock.replace(/_(.+?)_/g, '<em>$1</em>');

    // Escape remaining HTML but preserve our tags
    const tempMarker = '___TEMP_TAG___';
    processedBlock = processedBlock.replace(/<(strong|em)>/g, `${tempMarker}$1>`);
    processedBlock = processedBlock.replace(/<\/(strong|em)>/g, `${tempMarker}/$1>`);
    processedBlock = escapeHtml(processedBlock);
    processedBlock = processedBlock.replace(new RegExp(tempMarker, 'g'), '<');

    htmlBlocks.push(`<p>${processedBlock}</p>`);
  }

  return htmlBlocks.join('\n');
}

/**
 * Escapes HTML special characters
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Ensures content is in HTML format, converting if necessary
 */
export function ensureHtmlContent(content: string): string {
  if (!content) return '';
  return isHtmlContent(content) ? content : convertTextToHtml(content);
}

/**
 * Cleans up HTML content (removes extra whitespace, normalizes tags)
 */
export function cleanHtmlContent(html: string): string {
  if (!html) return '';

  // Remove multiple consecutive <br> tags
  html = html.replace(/(<br\s*\/?>){3,}/gi, '<br><br>');

  // Remove empty paragraphs
  html = html.replace(/<p>\s*<\/p>/gi, '');

  // Remove empty headings
  html = html.replace(/<h[1-6]>\s*<\/h[1-6]>/gi, '');

  // Normalize whitespace inside tags
  html = html.replace(/>(\s+)</g, '><');

  return html.trim();
}
